import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  AnchorLink,
  Button,
  Container,
  Icon,
  IconList,
  Input,
  Layout,
  Padding,
  Text,
} from "@kidslivesafe/sc-react";
import {
  useAppDispatch,
  useAppSelector,
  useHandlePageNavigate,
} from "../../../hooks/hooks";
import ProtectionShell from "./ProtectionShell";
import {
  selectProtectionRequestStatus,
  selectProtectionState,
  sendProtectionVerificationCodeAsync,
  setVerificationEmail,
} from "../../../features/profileProtection/profileProtectionSlice";
import { sendMemberEventAsync } from "../../../features/admin/adminSlice";
import { selectPeopleReport } from "../../../features/reports/reportsSlice";
import { verifyEmail } from "../../../utils/verifyEmail";
import { useCurrentBreakpoint } from "@kidslivesafe/sc-foundation";

const InfoCollection: React.FC = () => {
  const dispatch = useAppDispatch();
  const handleNavigate = useHandlePageNavigate();
  const { verificationEmail } = useAppSelector(selectProtectionState);
  const requestStatus = useAppSelector(selectProtectionRequestStatus);
  const pulledReport = useAppSelector(selectPeopleReport);
  const reportEmails = useMemo(() => {
    if (!pulledReport || !pulledReport.emailAddresses) {
      return [] as string[];
    }

    return pulledReport.emailAddresses
      .map(email => email.emailAddress)
      .filter(email => email && email.trim().length > 0)
      .filter((value, index, array) => {
        return array.indexOf(value) === index;
      });
  }, [pulledReport]);

  const [selectedEmail, setSelectedEmail] = useState<string>(verificationEmail);
  const [customEmail, setCustomEmail] = useState<string>("");
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [addedEmails, setAddedEmails] = useState<string[]>([]);
  const [emailError, setEmailError] = useState<string>("");
  const hasSentInfoCollectionViewedEvent = useRef(false);
  const hasSentEmailSubmittedEvent = useRef(false);

  const currentBreakpoint = useCurrentBreakpoint();

  useEffect(() => {
    if (!hasSentInfoCollectionViewedEvent.current) {
      dispatch(
        sendMemberEventAsync({
          EventCategory: "Profile Protection",
          EventAction: "Info Collection Viewed",
        }),
      );
      hasSentInfoCollectionViewedEvent.current = true;
    }
  }, [dispatch]);

  const emailOptions = useMemo(() => {
    const options = [...reportEmails, verificationEmail, ...addedEmails].filter(
      Boolean,
    ) as string[];
    return options.filter((value, index, array) => {
      return array.indexOf(value) === index;
    });
  }, [reportEmails, verificationEmail, addedEmails]);

  const handleAddEmail = () => {
    const trimmed = customEmail.trim();
    if (!trimmed) return;
    if (!verifyEmail(trimmed)) {
      setEmailError("Please enter a valid email address.");
      return;
    }
    setEmailError("");
    setAddedEmails(prev => [...prev, trimmed]);
    setSelectedEmail(trimmed);
    setCustomEmail("");
    setShowCustomInput(false);
  };

  const handleCancelAdd = () => {
    setCustomEmail("");
    setEmailError("");
    setShowCustomInput(false);
  };

  useEffect(() => {
    if (!selectedEmail && emailOptions.length > 0) {
      setSelectedEmail(emailOptions[0]);
    }
  }, [selectedEmail, emailOptions]);

  const handleContinue = async () => {
    const finalEmail = customEmail.trim() || selectedEmail;
    const verifiedEmail = verifyEmail(finalEmail);
    if (!verifiedEmail) {
      setEmailError("Please enter a valid email address.");
      return;
    }
    if (!finalEmail) {
      return;
    }
    dispatch(setVerificationEmail(finalEmail));

    try {
      await dispatch(
        sendProtectionVerificationCodeAsync({
          email: finalEmail,
        }),
      ).unwrap();
    } catch {
      return;
    }

    if (!hasSentEmailSubmittedEvent.current) {
      dispatch(
        sendMemberEventAsync({
          EventCategory: "Profile Protection",
          EventAction: "Email Submitted",
        }),
      );
      hasSentEmailSubmittedEvent.current = true;
    }

    handleNavigate("/protection/verify");
  };

  return (
    <ProtectionShell
      title="Confirm your details"
      subtitle="This one-time validation step verifies we have the correct profile for removal. Once verified, all future communications will continue to be sent to the email address associated with your membership account."
      stepLabel="Step 2 of 3"
    >
      <Padding top="md">
        <Layout type="flex" flexProps={{ direction: "column", gap: "xl" }}>
          <Container border bodyBorderRadius borderColor="primary">
            <Padding space="lg">
              <Layout type="flex" flexProps={{ direction: "row" }}>
                <Padding right="md" top="xxs">
                  <Icon
                    name={IconList.IconMultipleStack}
                    size={30}
                    iconColor="var(--sc-text-color-primary)"
                  />
                </Padding>
                <Padding space="sm" top="none">
                  <Layout
                    type="flex"
                    flexProps={{ direction: "column", gap: "sm" }}
                  >
                    <Text fontWeight="semibold">Verification Email</Text>
                    <Text size="sm" color="dark-gray">
                      Some data brokers require email verification. Select an
                      email you have access to.
                    </Text>
                    {emailOptions.length === 0 ? (
                      <Text size="xs" color="dark-gray">
                        No email found on this report. Add your own below.
                      </Text>
                    ) : null}
                    {emailOptions.map(email => (
                      <label key={email}>
                        <Layout
                          type="flex"
                          flexProps={{ gap: "xs", alignItems: "center" }}
                        >
                          <input
                            type="radio"
                            name="protection-email"
                            value={email}
                            checked={selectedEmail === email}
                            onChange={() => setSelectedEmail(email)}
                          />
                          <Text size="sm">{email}</Text>
                        </Layout>
                      </label>
                    ))}
                    {showCustomInput ? (
                      <Layout
                        type="flex"
                        flexProps={{
                          direction: "row",
                          gap: "xxs",
                        }}
                      >
                        <Layout
                          type="flex"
                          flexProps={{
                            gap: "xs",
                            alignItems: "center",
                            direction: "column",
                          }}
                          breakpointStyles={{
                            desktop: {
                              type: "flex",
                              flexProps: {
                                direction: "row",
                                gap: "xs",
                              },
                            },
                          }}
                          fillHorizontal
                        >
                          <Input
                            id="protection-custom-email"
                            type="email"
                            placeholder="Add your email"
                            value={customEmail}
                            fill="fill"
                            handleChange={(val: string) => {
                              setCustomEmail(val);
                              if (emailError) setEmailError("");
                            }}
                            error={!!emailError}
                          />
                          <Button
                            buttonText="Add"
                            color="primary"
                            fill={
                              currentBreakpoint === "desktop"
                                ? "fit-content"
                                : "fill"
                            }
                            disabled={!customEmail.trim()}
                            handleClick={handleAddEmail}
                          />
                          <Button
                            buttonText="Cancel"
                            color="secondary"
                            fill={
                              currentBreakpoint === "desktop"
                                ? "fit-content"
                                : "fill"
                            }
                            handleClick={handleCancelAdd}
                          />
                        </Layout>
                        {emailError && (
                          <Text size="xs" color="error">
                            {emailError}
                          </Text>
                        )}
                      </Layout>
                    ) : (
                      <Layout
                        type="flex"
                        flexProps={{ gap: "xxs", alignItems: "center" }}
                      >
                        <span
                          onClick={() => setShowCustomInput(true)}
                          style={{
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                          }}
                        >
                          <Icon name={IconList.IconPlus} size={16} />
                          <Text size="sm" color="primary">
                            Add your email
                          </Text>
                        </span>
                      </Layout>
                    )}
                    <Padding top="md">
                      <Button
                        buttonText={
                          requestStatus === "loading"
                            ? "Sending code..."
                            : "Continue"
                        }
                        color="primary"
                        fill="fill"
                        disabled={
                          !(customEmail.trim() || selectedEmail) ||
                          requestStatus === "loading"
                        }
                        loading={requestStatus === "loading"}
                        handleClick={handleContinue}
                      />
                    </Padding>
                  </Layout>
                </Padding>
              </Layout>
            </Padding>
          </Container>

          <Layout
            type="flex"
            flexProps={{ justifyContent: "center", gap: "sm" }}
          >
            <AnchorLink
              role="button"
              handleClick={() => handleNavigate("/protection/scan")}
            >
              Back to scan results
            </AnchorLink>
          </Layout>
        </Layout>
      </Padding>
    </ProtectionShell>
  );
};

export default InfoCollection;
